import { html, render, svg, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-tenant-card.css?inline';

/**
 * `<pk-tenant-card>` — tenant summary tile for the admin tenants list.
 *
 * Renders the tenant name + tier, byte-formatted usage, an inline sparkline
 * built from `sparkline-buckets`, the member count, and a soft warning when
 * `scheduled-deletion-date` is set.
 *
 * Attributes (all reflected from the spec at §9.1):
 *   - `name`                     — tenant display name. Required.
 *   - `tier`                     — `free` | `pro` | `enterprise` | other string;
 *                                   appears as a chip beside the name.
 *   - `usage-bytes`              — non-negative integer; formatted as B/KB/MB/GB.
 *   - `sparkline-buckets`        — JSON-encoded array of numbers, e.g.
 *                                   `"[12, 14, 18, 9, 22]"`. The first / last
 *                                   value drive the up/down accent. Empty array
 *                                   suppresses the sparkline.
 *   - `members-count`            — non-negative integer.
 *   - `scheduled-deletion-date`  — optional ISO date string; when present the
 *                                   card grows a "scheduled for deletion" warn
 *                                   strip.
 *
 * Events:
 *   - `pk-tenant-action` — `CustomEvent<{ action: string }>` fired when any
 *                          element with a `data-action="<name>"` attribute
 *                          inside the card is clicked. The card itself renders
 *                          default `View` / `Manage` buttons; consumers can
 *                          slot additional `data-action` buttons via the
 *                          `actions` slot.
 *
 * Slots:
 *   - `actions` — optional extra buttons in the actions row.
 */
export class PkTenantCard extends HTMLElement {
    static readonly tagName = 'pk-tenant-card';

    static get observedAttributes(): string[] {
        return [
            'name', 'tier', 'usage-bytes', 'sparkline-buckets',
            'members-count', 'scheduled-deletion-date',
        ];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this.addEventListener('click', this._onClick);
    }

    disconnectedCallback(): void {
        this.removeEventListener('click', this._onClick);
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private readonly _onClick = (e: MouseEvent): void => {
        const path = e.composedPath();
        for (const target of path) {
            if (target instanceof Element) {
                const action = target.getAttribute('data-action');
                if (action) {
                    this.dispatchEvent(new CustomEvent('pk-tenant-action', {
                        detail:   { action },
                        bubbles:  true,
                        composed: true,
                    }));
                    return;
                }
            }
        }
    };

    private _parseBuckets(): number[] {
        const raw = this.getAttribute('sparkline-buckets');
        if (!raw) return [];
        try {
            const parsed: unknown = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.map(v => Number(v)).filter(n => Number.isFinite(n));
            }
        } catch { /* fall through */ }
        return [];
    }

    private _fmtBytes(): string {
        const raw = this.getAttribute('usage-bytes');
        if (!raw) return '0 B';
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) return '—';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
        return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }

    private _sparklineTpl(buckets: number[]): TemplateResult | typeof nothing {
        if (buckets.length === 0) return nothing;
        const W = 96;
        const H = 28;
        const max = Math.max(...buckets, 1);
        const step = buckets.length > 1 ? W / (buckets.length - 1) : W;
        const pts = buckets
            .map((v, i) => `${(buckets.length === 1 ? W / 2 : i * step).toFixed(1)},${(H - (v / max) * H).toFixed(1)}`)
            .join(' ');
        const trendUp = buckets.length > 1 && buckets[buckets.length - 1]! >= buckets[0]!;
        const stroke = trendUp
            ? 'var(--pk-color-success, #2A7E4F)'
            : 'var(--pk-color-danger, #A23131)';
        return html`
            <svg class="sparkline" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}"
                 aria-hidden="true" role="img">
                ${svg`<polyline points=${pts} fill="none" stroke=${stroke} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />`}
            </svg>
        `;
    }

    private template(): TemplateResult {
        const name      = this.getAttribute('name')        ?? '';
        const tier      = this.getAttribute('tier')        ?? '';
        const members   = this.getAttribute('members-count') ?? '0';
        const deletion  = this.getAttribute('scheduled-deletion-date') ?? '';
        const usage     = this._fmtBytes();
        const buckets   = this._parseBuckets();
        const spark     = this._sparklineTpl(buckets);

        return html`
            <style>${styles}</style>
            <article class="card ${deletion ? 'scheduled-deletion' : ''}">
                <header class="header">
                    <h3 class="name">${name}</h3>
                    ${tier ? html`<span class="tier tier-${tier.toLowerCase()}">${tier}</span>` : ''}
                </header>
                <div class="metrics">
                    <div class="metric">
                        <span class="metric-label">Usage</span>
                        <span class="metric-value">${usage}</span>
                    </div>
                    <div class="metric">
                        <span class="metric-label">Members</span>
                        <span class="metric-value">${members}</span>
                    </div>
                    <div class="metric metric-spark">
                        <span class="metric-label">Activity</span>
                        ${buckets.length === 0
                            ? html`<span class="metric-value metric-value-muted">—</span>`
                            : spark}
                    </div>
                </div>
                ${deletion ? html`
                    <div class="deletion-warning" role="alert">
                        Scheduled for deletion on <strong>${deletion}</strong>
                    </div>
                ` : ''}
                <footer class="actions">
                    <button type="button" class="action-btn" data-action="view">View</button>
                    <button type="button" class="action-btn" data-action="manage">Manage</button>
                    <slot name="actions"></slot>
                </footer>
            </article>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkTenantCard.tagName)) {
    customElements.define(PkTenantCard.tagName, PkTenantCard);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-tenant-card': PkTenantCard;
    }
}
